import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { createAlternatePlan } from "../../lib/api";
import AltPlanFields from "../students/AltPlanFields";

const EMPTY = { plan_type: "weekly", days: [], dates: [], start_date: "", end_date: "", arrival_time: "", departure_time: "", destination: "", notes: "", full_day_out: false };

/**
 * Shortcut "Add Alternate Plan" dialog opened directly from the Master Schedule page,
 * so staff don't have to open a student file and scroll to the Schedule tab.
 * Adds a student picker on top of the shared AltPlanFields form.
 */
export default function AddAltPlanDialog({ open, onOpenChange, studentList = [], studentLookup = {}, blocks = [], onChanged }) {
  const [studentId, setStudentId] = useState("");
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setStudentId(""); setForm({ ...EMPTY }); }
  }, [open]);

  const handleSave = async () => {
    if (!studentId) { toast.error("Please select a student"); return; }
    const isDates = form.plan_type === "dates";
    const isRange = form.plan_type === "range";
    const hasWindow = form.arrival_time || form.departure_time;
    const fullOut = !!form.full_day_out;
    const scheduleMissing = isRange ? (!form.start_date || !form.end_date) : isDates ? !form.dates.length : !form.days.length;
    if (scheduleMissing || (!fullOut && !hasWindow) || !form.destination) {
      toast.error(isRange ? "Please pick a start and end date, a destination, and an arrival/departure time (unless full day out)" : isDates ? "Please add dates, a destination, and an arrival/departure time (unless full day out)" : "Please fill in days, a destination, and an arrival/departure time (unless full day out)");
      return;
    }
    if (isRange && form.start_date && form.end_date && form.end_date < form.start_date) {
      toast.error("End date must be on or after the start date");
      return;
    }
    if (!fullOut && form.arrival_time && form.departure_time && form.departure_time <= form.arrival_time) {
      toast.error("Departure time must be after arrival time");
      return;
    }
    setSaving(true);
    try {
      await createAlternatePlan({ ...form, student_id: studentId });
      toast.success("Alternate plan created");
      onChanged && (await onChanged());
      onOpenChange(false);
    } catch {
      toast.error("Failed to create alternate plan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="add-alt-plan-dialog">
        <DialogHeader><DialogTitle>Add Alternate Plan</DialogTitle><DialogDescription>Create an internship/off-site plan for a student without leaving the schedule.</DialogDescription></DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label>Student *</Label>
            <Select value={studentId} onValueChange={setStudentId}>
              <SelectTrigger data-testid="add-alt-plan-student-select"><SelectValue placeholder="Select a student..." /></SelectTrigger>
              <SelectContent>
                {studentList.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{studentLookup[s.id] || s.preferred_name || `${s.first_name} ${s.last_name}`}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AltPlanFields form={form} setForm={setForm} blocks={blocks} testidPrefix="add-alt-" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={handleSave} disabled={saving} data-testid="save-add-alt-plan-btn">{saving ? "Saving..." : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
